import {
  parsearFecha,
  parsearMonto,
  montosEnLinea,
  limpiarConcepto,
  round2,
  fechaDesdePartes,
  crearFiltroPagina,
  armarDetalle,
  quitarUltimosMontos,
} from "./helpers";

/**
 * Plantilla de BANORTE (Banco Mercantil del Norte).
 *
 * Formato de "DETALLE DE MOVIMIENTOS":
 *   FECHA | DESCRIPCIÓN / ESTABLECIMIENTO | MONTO DEL DEPÓSITO | MONTO DEL RETIRO | SALDO
 *
 * Retos propios de Banorte:
 *  - La fecha "DD-MMM-YY" viene PEGADA a la descripción ("12-MAR-26058-...").
 *  - Movimientos de varias líneas: la descripción se parte y los IMPORTES
 *    (monto + saldo) caen en un renglón aparte al final del bloque.
 *  - Importes sin "$" y columnas que se colapsan al extraer texto, así que el tipo
 *    se deduce por la variación del saldo (sube = depósito/cargo, baja = retiro/abono).
 *  - Hay varias subcuentas (Cta Enlace / Inversión), cada una arranca con un renglón
 *    "SALDO ANTERIOR" que reinicia la cadena de saldos.
 */

// Fecha al inicio del renglón ("12-MAR-26"), normalmente pegada al concepto.
const RE_FECHA_INICIO = /^(\d{1,2}-[A-Za-zÁÉÍÓÚáéíóú]{3}-\d{2})/;
const RE_DETALLE = /detalle de movimientos/i;
const RE_FIN = /^\s*otros\b/i; // sección "OTROS" cierra el detalle
const RE_SALDO_ANT = /saldo\s+anterior/i;
const RE_PRIMER_MONTO = /-?\(?\$?\s?\d{1,3}(?:,\d{3})*\.\d{2}/;
// Signo negativo AL FINAL de un importe ("11,000.00- 1,375,979.15"): sin quitarlo,
// REGEX_MONTO lo toma como signo del saldo siguiente (-1,375,979.15).
const RE_MENOS_FINAL = /(\d\.\d{2})\s?-(?=\s|$)/g;
// Títulos de subcuenta que se repiten en cada hoja (no son parte de un movimiento).
const RE_TITULO_SUBCUENTA = /^(enlace negocios|inversi[oó]n enlace negocios)\b/i;

const banorte = {
  id: "banorte",
  nombre: "Banorte",
  clavesSat: ["072"],
  // Detección específica: evita falsos positivos cuando "BANORTE" aparece sólo
  // como contraparte de un SPEI/TEF en el estado de cuenta de OTRO banco.
  detectar: (texto) => /banco mercantil del norte|bmn930209927|banortel/i.test(texto),
  // Devoluciones: un SPEI rechazado ("DEV.SPEI ... MOT DEV:2 CUENTA BLOQUEADA")
  // regresa en la columna de RETIROS con signo negativo ("11,000.00-") y trae la
  // misma clave de rastreo que el retiro original ("CVE RASTREO:" / "CVE.RASTR:").
  reversos: {
    montoNegativo: /\d\.\d{2}\s?-(?=\s|$)/, // misma forma que RE_MENOS_FINAL (sin /g)
    rastreo: /CVE\.?\s*RASTR(?:EO)?\s*:\s*([A-Z0-9]{10,})/i,
  },
  // Encabezado: período "Del 01/Marzo/2026 al 31/Marzo/2026" + saldo actual.
  extraerEncabezado(lineas) {
    const t = (lineas || []).join("\n");
    const per = t.match(
      /del\s+(\d{1,2})\/([a-záéíóú]+)\/(\d{2,4})\s+al\s+(\d{1,2})\/([a-záéíóú]+)\/(\d{2,4})/i
    );
    const sf = t.match(/saldo\s+actual\b[^$\d]*\$?\s*(-?\d{1,3}(?:,\d{3})*\.\d{2})/i);
    return {
      fechaInicial: per ? fechaDesdePartes(per[1], per[2], per[3]) : null,
      fechaFinal: per ? fechaDesdePartes(per[4], per[5], per[6]) : null,
      saldoFinal: sf ? parsearMonto(sf[1]) : null,
    };
  },
  parsear(lineas, anioRef) {
    const arr = lineas || [];

    // Acotar a la sección de movimientos (de "DETALLE DE MOVIMIENTOS" a "OTROS").
    let ini = arr.findIndex((l) => RE_DETALLE.test(l));
    let fin = arr.length;
    for (let i = ini + 1; i < arr.length; i++) {
      if (RE_FIN.test(arr[i])) { fin = i; break; }
    }
    const seccion = arr.slice(ini + 1, fin);
    const idxPrimerMov = seccion.findIndex((l) => RE_FECHA_INICIO.test(l));
    const esDePagina = crearFiltroPagina(
      arr,
      idxPrimerMov > -1 ? ini + 1 + idxPrimerMov : -1,
      RE_TITULO_SUBCUENTA
    );

    // Saldo inicial de respaldo (por si no se captura el renglón "SALDO ANTERIOR").
    let prevSaldo = null;
    for (const l of arr) {
      const m = l.match(/saldo\s+inicial\s+del\s+periodo\s*\$?\s*(-?\d{1,3}(?:,\d{3})*\.\d{2})/i);
      if (m) { prevSaldo = parsearMonto(m[1]); break; }
    }

    // Bloques: cada fecha inicia un movimiento; se acumulan las líneas siguientes
    // hasta tener >= 2 importes (monto + saldo), porque a veces caen en otra línea.
    const registros = [];
    let cur = null;
    for (const linea of seccion) {
      if (RE_FECHA_INICIO.test(linea)) {
        cur = { texto: linea, extras: [] };
        registros.push(cur);
      } else if (cur && montosEnLinea(cur.texto).length < 2) {
        cur.texto += " " + linea;
      } else if (cur && !esDePagina(linea)) {
        // Resto de la descripción cuando los importes ya llegaron: no se mezcla con
        // los importes; completa el detalle y trae la clave de rastreo (devoluciones).
        cur.extras.push(linea);
      }
    }

    const movimientos = [];
    for (const r of registros) {
      // El tipo sale de la variación del saldo, así que el signo del monto sobra.
      const ms = montosEnLinea(r.texto.replace(RE_MENOS_FINAL, "$1"));

      // "SALDO ANTERIOR" reinicia la cadena de saldos (arranque de cada subcuenta).
      if (RE_SALDO_ANT.test(r.texto)) {
        if (ms.length) prevSaldo = ms[ms.length - 1];
        continue;
      }
      if (ms.length < 2) continue;

      const mFecha = r.texto.match(RE_FECHA_INICIO);
      const fecha = parsearFecha(mFecha[1], anioRef, ["DD-MMM-YY", "DD-MMM-YYYY"]);
      const saldo = ms[ms.length - 1];
      const monto = Math.abs(ms[ms.length - 2]);

      let tipo = null;
      if (prevSaldo != null) {
        tipo = round2(saldo - prevSaldo) >= 0 ? "cargo" : "abono";
      }
      prevSaldo = saldo;

      const idxMonto = r.texto.search(RE_PRIMER_MONTO);
      const concepto = limpiarConcepto(
        r.texto.slice(mFecha[0].length, idxMonto > -1 ? idxMonto : undefined)
      );

      movimientos.push({
        fecha,
        monto: round2(monto),
        tipo,
        concepto,
        // Bloque completo sin el importe + saldo del final, más los renglones de detalle.
        detalle: armarDetalle([
          quitarUltimosMontos(r.texto.slice(mFecha[0].length), 2),
          ...r.extras,
        ]),
        saldo,
        raw: r.texto,
        rawExtra: r.extras.join(" "),
      });
    }

    return movimientos;
  },
};

export default banorte;
